import Alpine from 'alpinejs';
import Vivus from 'vivus';

/**
 * Product Hero Carousel
 *
 * Alpine.js data component for the product hero section.
 * Handles:
 * - Image carousel with thumbnail click-to-select
 * - Prev/next navigation with infinite loop
 * - Vivus SVG wavy line animation triggered by IntersectionObserver
 *
 * The SVGs live inside a <shopify-context><template> which renders
 * asynchronously after the Shopify web component fetches product data.
 * A MutationObserver watches for the SVG elements to appear in the DOM
 * before initialising Vivus animations.
 */
Alpine.data('productHeroCarousel', (slideCount = 1) => ({
    activeSlide: 0,
    totalSlides: slideCount,
    vivusInstances: [],
    vivusAnimated: false,
    moreInfoOpen: false,

    init() {
        // SVGs are inside <template> tags rendered by Shopify web components.
        // They won't exist in the DOM yet, so we watch for them to appear.
        this.waitForSvgs();
    },

    /**
     * Navigate to a specific slide by index
     */
    goToSlide(index) {
        this.activeSlide = index;
    },

    /**
     * Go to the next slide (loops back to 0)
     */
    nextSlide() {
        this.activeSlide = (this.activeSlide + 1) % this.totalSlides;
    },

    /**
     * Go to the previous slide (loops to last)
     */
    prevSlide() {
        this.activeSlide = (this.activeSlide - 1 + this.totalSlides) % this.totalSlides;
    },

    /**
     * Watch for SVG elements to appear in the DOM (after Shopify template renders),
     * then set up the IntersectionObserver for scroll-triggered animation.
     */
    waitForSvgs() {
        const heroEl = this.$el;

        // Check if SVGs already exist (template may have rendered before Alpine init)
        const existingSvgs = heroEl.querySelectorAll('.product-hero__vivus-svg');
        if (existingSvgs.length > 0) {
            this.initVivusObserver(existingSvgs);
            return;
        }

        // Watch for the Shopify template to render and add SVGs to the DOM
        const mutationObserver = new MutationObserver(() => {
            const svgs = heroEl.querySelectorAll('.product-hero__vivus-svg');
            if (svgs.length > 0) {
                mutationObserver.disconnect();
                this.initVivusObserver(svgs);
            }
        });

        mutationObserver.observe(heroEl, {
            childList: true,
            subtree: true
        });
    },

    /**
     * Set up IntersectionObserver to trigger Vivus animation
     * when the product hero section scrolls into view.
     */
    initVivusObserver(svgElements) {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !this.vivusAnimated) {
                    this.vivusAnimated = true;
                    this.animateSvgLines(svgElements);
                    observer.disconnect();
                }
            });
        }, {
            threshold: 0.2
        });

        observer.observe(this.$el);
    },

    /**
     * Animate each SVG wavy line using Vivus.js.
     * Lines animate sequentially with a staggered delay.
     * Paths are hidden via CSS until Vivus is ready to draw them.
     */
    animateSvgLines(svgElements) {
        svgElements.forEach((svg, index) => {
            const delay = index * 400; // 400ms stagger between each line

            setTimeout(() => {
                try {
                    // Add class to make paths visible (CSS hides them initially)
                    svg.classList.add('vivus-active');

                    const vivusInstance = new Vivus(svg, {
                        type: 'oneByOne',
                        duration: 50,
                        animTimingFunction: Vivus.EASE,
                        start: 'autostart'
                    });

                    this.vivusInstances.push(vivusInstance);
                } catch (e) {
                    // If Vivus fails, still show the SVG paths
                    console.warn('Vivus animation failed for SVG:', e);
                    svg.classList.add('vivus-active');
                }
            }, delay);
        });
    }
}));